import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, Calendar, Users, UserCog, CheckCircle, Clock, ChevronDown, ChevronUp, RefreshCw, Sliders } from "lucide-react";
import api, { apiErrorMsg } from "@/lib/api";

export default function PeriodePage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [pengasuhList, setPengasuhList] = useState([]);
  const [kelompokList, setKelompokList] = useState([]);
  const [indikator, setIndikator] = useState([]);
  const [defaultFixedZero, setDefaultFixedZero] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showObsConfig, setShowObsConfig] = useState(false);
  const [form, setForm] = useState({ jenis: "mingguan", nomor: 1, bulan: "Januari", tahun: "2026", siswa_ids: [], pengasuh_id: "", kelompok_id: "", nama_periode: "", jml_obs: [], fixed_zero: [] });

  const load = () => {
    api.get("/admin/periode").then((r) => setList(r.data)).catch((e) => toast.error(apiErrorMsg(e)));
  };

  useEffect(() => {
    load();
    api.get("/admin/siswa").then((r) => setSiswaList(r.data));
    api.get("/admin/pengasuh").then((r) => setPengasuhList(r.data));
    api.get("/admin/kelompok").then((r) => setKelompokList(r.data));
    // Ambil default JML OBS & SELALU 0 dari /constants; digunakan sebagai default nilai form
    api.get("/constants").then((r) => {
      setIndikator(r.data.indikator_observasi || []);
      const defFz = new Set(r.data.default_fixed_zero || []);
      setDefaultFixedZero(r.data.default_fixed_zero || []);
      setForm((f) => ({
        ...f,
        jml_obs: (r.data.indikator_observasi || []).map((i) => i.jml_obs),
        fixed_zero: (r.data.indikator_observasi || []).map((i) => defFz.has(i.no)),
      }));
    });
  }, []);

  // Saat kelompok dipilih, auto-fill siswa_ids
  useEffect(() => {
    if (form.kelompok_id) {
      const k = kelompokList.find((x) => x.id === form.kelompok_id);
      if (k) setForm((f) => ({ ...f, siswa_ids: k.siswa_ids || [], nama_periode: f.nama_periode || k.nama }));
    }
  }, [form.kelompok_id, kelompokList]);

  const resetObsDefault = () => {
    const defFz = new Set(defaultFixedZero);
    setForm((f) => ({
      ...f,
      jml_obs: indikator.map((i) => i.jml_obs),
      fixed_zero: indikator.map((i) => defFz.has(i.no)),
    }));
    toast.success("JML OBS & SELALU 0 direset ke default");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.siswa_ids.length < 4) return toast.error("Pilih minimal 4 serdik");
    if (!form.pengasuh_id) return toast.error("Pilih pengasuh");
    try {
      await api.post("/admin/periode", { ...form, nomor: Number(form.nomor) });
      toast.success("Jadwal dibuat — JML OBS & SELALU 0 tersimpan pada periode ini");
      setCreating(false);
      setShowObsConfig(false);
      const defFz = new Set(defaultFixedZero);
      setForm({
        jenis: "mingguan", nomor: 1, bulan: "Januari", tahun: "2026",
        siswa_ids: [], pengasuh_id: "", kelompok_id: "", nama_periode: "",
        jml_obs: indikator.map((i) => i.jml_obs),
        fixed_zero: indikator.map((i) => defFz.has(i.no)),
      });
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus jadwal beserta semua data submission?")) return;
    try {
      await api.delete(`/admin/periode/${id}`);
      toast.success("Dihapus");
      load();
    } catch (e) { toast.error(apiErrorMsg(e)); }
  };

  const toggleSiswa = (id) => {
    setForm((f) => ({ ...f, siswa_ids: f.siswa_ids.includes(id) ? f.siswa_ids.filter((x) => x !== id) : [...f.siswa_ids, id] }));
  };

  const selectAll = () => setForm((f) => ({ ...f, siswa_ids: siswaList.map((s) => s.id) }));
  const clearAll = () => setForm((f) => ({ ...f, siswa_ids: [] }));

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Penjadwalan</div>
          <h1 className="font-heading text-3xl font-bold text-navy-900">Jadwal Pengisian</h1>
          <p className="text-slate-500 mt-1 text-sm">Buat periode penilaian per minggu/bulan untuk kelompok Serdik.</p>
        </div>
        <button onClick={() => setCreating(!creating)} data-testid="periode-add-btn" className="bg-brass-500 hover:bg-brass-600 text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Buat Jadwal
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-brass-300 rounded-sm p-5 mb-6 animate-fade-in">
          <h3 className="font-heading font-bold text-navy-900 mb-4">Periode Baru</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Jenis</label>
              <select value={form.jenis} onChange={(e) => setForm({ ...form, jenis: e.target.value })} data-testid="periode-jenis" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="mingguan">Mingguan</option>
                <option value="bulanan">Bulanan</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Ke-</label>
              <input type="number" min="1" value={form.nomor} onChange={(e) => setForm({ ...form, nomor: e.target.value })} data-testid="periode-nomor" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Bulan</label>
              <input value={form.bulan} onChange={(e) => setForm({ ...form, bulan: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Tahun</label>
              <input value={form.tahun} onChange={(e) => setForm({ ...form, tahun: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Pilih Kelompok (opsional — otomatis isi daftar siswa)</label>
            <select value={form.kelompok_id} onChange={(e) => setForm({ ...form, kelompok_id: e.target.value })} data-testid="periode-kelompok" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">-- Tidak pakai kelompok (pilih manual) --</option>
              {kelompokList.map((k) => <option key={k.id} value={k.id}>{k.nama} ({k.siswa_count} siswa)</option>)}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Pengasuh Penanggung Jawab</label>
            <select value={form.pengasuh_id} onChange={(e) => setForm({ ...form, pengasuh_id: e.target.value })} data-testid="periode-pengasuh" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              <option value="">-- Pilih pengasuh --</option>
              {pengasuhList.map((p) => <option key={p.id} value={p.id}>{p.nama}</option>)}
            </select>
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase font-semibold text-slate-500">Kelompok Serdik (pilih minimal 4)</label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={selectAll} className="text-brass-600 hover:underline">Pilih semua</button>
                <span className="text-slate-300">|</span>
                <button type="button" onClick={clearAll} className="text-slate-500 hover:underline">Kosongkan</button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-sm p-2 grid grid-cols-2 md:grid-cols-3 gap-1 bg-slate-50" data-testid="periode-siswa-grid">
              {siswaList.map((s) => {
                const checked = form.siswa_ids.includes(s.id);
                return (
                  <label key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-sm ${checked ? "bg-brass-100 border border-brass-300" : "bg-white border border-slate-200 hover:border-slate-300"}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSiswa(s.id)} className="accent-brass-500" />
                    <span className="truncate">{s.nama}</span>
                  </label>
                );
              })}
              {siswaList.length === 0 && <div className="col-span-3 text-center text-slate-400 py-4">Belum ada serdik terdaftar.</div>}
            </div>
            <div className="text-xs text-slate-500 mt-1">Terpilih: <span className="font-bold font-mono text-navy-900">{form.siswa_ids.length}</span></div>
          </div>

          {/* Pengaturan Observasi per-periode: JML OBS & Toggle SELALU 0 */}
          <div className="mb-4 border border-slate-200 rounded-sm bg-slate-50">
            <button
              type="button"
              onClick={() => setShowObsConfig((x) => !x)}
              data-testid="periode-obs-config-toggle"
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-100 text-left"
            >
              <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-brass-500" />
                <div>
                  <div className="text-sm font-semibold text-navy-900">Pengaturan Observasi (JML OBS &amp; SELALU 0) — 59 indikator</div>
                  <div className="text-xs text-slate-500">Nilai default sudah diisi. Silakan sesuaikan bila perlu — akan tersimpan pada periode ini.</div>
                </div>
              </div>
              {showObsConfig ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showObsConfig && (
              <div className="border-t border-slate-200 p-4 bg-white">
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={resetObsDefault}
                    data-testid="periode-obs-reset-btn"
                    className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1"
                  ><RefreshCw className="w-3 h-3" /> Reset ke Default</button>
                </div>
                <div className="overflow-auto max-h-[400px] border border-slate-200 rounded-sm">
                  <table className="w-full text-sm" data-testid="periode-obs-table">
                    <thead className="sticky top-0">
                      <tr className="bg-slate-100 text-xs uppercase">
                        <th className="p-2 w-12 text-center">No</th>
                        <th className="p-2 text-left">Indikator</th>
                        <th className="p-2 w-16 text-center">Kode</th>
                        <th className="p-2 w-24 text-center">JML OBS</th>
                        <th className="p-2 w-24 text-center">SELALU 0</th>
                      </tr>
                    </thead>
                    <tbody>
                      {indikator.map((ind, i) => (
                        <tr key={ind.no} className={`border-t border-slate-100 ${form.fixed_zero?.[i] ? "bg-amber-50" : ""}`}>
                          <td className="p-2 text-center font-mono text-slate-500">{ind.no}</td>
                          <td className="p-2 text-xs">{ind.teks}</td>
                          <td className="p-2 text-center font-mono font-bold text-navy-900">{ind.kode}</td>
                          <td className="p-2 text-center">
                            <input
                              type="number" min="0"
                              value={form.jml_obs?.[i] ?? ind.jml_obs}
                              data-testid={`periode-obs-jml-${ind.no}`}
                              onChange={(e) => {
                                const next = [...(form.jml_obs || indikator.map((x) => x.jml_obs))];
                                next[i] = Number(e.target.value) || 0;
                                setForm({ ...form, jml_obs: next });
                              }}
                              className="w-16 px-2 py-1 border border-slate-300 rounded-sm text-sm font-mono text-center"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!form.fixed_zero?.[i]}
                              data-testid={`periode-obs-fz-${ind.no}`}
                              onChange={(e) => {
                                const next = [...(form.fixed_zero || indikator.map(() => false))];
                                next[i] = e.target.checked;
                                setForm({ ...form, fixed_zero: next });
                              }}
                              className="w-4 h-4 accent-brass-500"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-sm hover:bg-slate-50">Batal</button>
            <button type="submit" data-testid="periode-save-btn" className="bg-navy-900 hover:bg-navy-800 text-white px-5 py-2 rounded-sm text-sm font-semibold">Simpan Jadwal</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="periode-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wide">
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left p-3">Periode</th>
              <th className="text-left p-3">Kelompok</th>
              <th className="text-left p-3">Pengasuh</th>
              <th className="text-center p-3 w-32">Serdik</th>
              <th className="text-center p-3 w-32">Status</th>
              <th className="text-right p-3 w-32">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan="7" className="p-4 md:p-8 text-center text-slate-400">Belum ada jadwal.</td></tr>
            ) : list.map((p, i) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 text-slate-500 font-mono">{i + 1}</td>
                <td className="p-3">
                  <div className="font-semibold text-navy-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-brass-500" />
                    {p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{p.bulan} {p.tahun}</div>
                </td>
                <td className="p-3 text-slate-700">{p.kelompok_nama || <span className="text-slate-400">—</span>}</td>
                <td className="p-3"><span className="flex items-center gap-2"><UserCog className="w-4 h-4 text-slate-400" />{p.pengasuh_nama}</span></td>
                <td className="p-3 text-center font-mono"><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-400" />{p.siswa_ids?.length || 0}</span></td>
                <td className="p-3 text-center">
                  {p.status === "open" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold"><Clock className="w-3 h-3" /> Aktif</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-sm text-xs font-semibold"><CheckCircle className="w-3 h-3" /> Ditutup</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/admin/periode/${p.id}`} className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-sm flex items-center gap-1 text-xs"><ChevronRight className="w-4 h-4" /> Detail</Link>
                    <button onClick={() => remove(p.id)} className="p-1.5 text-red-700 hover:bg-red-50 rounded-sm" title="Hapus"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
